<template>
  <div ref="dataViewRow" class="widget" @click.prevent="editStream">
    <div class="row-content grid" :style="`grid-template-columns:  ${columnsGridTemplate(props.columns, true)};`">
      <div class="title" :class="shouldHideOnBreakpoint(props.columns, 'title')">
        <NewTooltip :placement="'top'" :content="title" auto-ellipsis @click.prevent="editStream" />
      </div>
      <div class="head date" :class="shouldHideOnBreakpoint(props.columns, 'created_date')">{{ createdDate }}</div>
      <div class="head date" :class="shouldHideOnBreakpoint(props.columns, 'last_run')">{{ lastRun }}</div>
      <div class="head date" :class="shouldHideOnBreakpoint(props.columns, 'last_run_feed_number_of_errors')">{{ lastRunNumOfErrors }}</div>
      <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'is_public')">
        <Label
          :text="$t('settings-sources-public-' + item.is_public)"
          :color="getPublicStatusColor(item.is_public)"
          :size="LabelSizes.Large"
        />
      </div>
      <div class="folder head" :class="shouldHideOnBreakpoint(props.columns, 'folder')">
        <NewTooltip
          v-if="folderName !== EMPTY_FIELD" :placement="'top'" :content="folderName" :textLength="15"
          @click.prevent="editStream"
        />

        <div v-else class="flex">
          {{ shortenText(folderName, 15) }}
        </div>
      </div>
      <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'category')">
        <div class="category category-bg">{{ categoryName }}</div>
      </div>
      <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'type')">
        <div class="type">{{ $t('settings-sources-type-' + item.type) }}</div>
      </div>
      <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'status')">
        <Label
          :text="$t('settings-sources-active-' + item.status)"
          :color="getStatusColor(item.status)"
          :size="LabelSizes.Large"
        />
      </div>
      <div class="d-flex flex-xs-justify-end" :class="shouldHideOnBreakpoint(props.columns, 'actions')">
        <ActionButtons :actions="actions" :is-visible="isRowHovered" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { NewTooltip } from '@/components/common'
import { getDateString, openModalCustomConfirmation, shortenText } from '@/services/utils'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import { columnsGridTemplate, shouldHideOnBreakpoint } from '@/services/layoutUtils'
import { computed, inject, ref, markRaw } from 'vue'
import { useElementHover } from '@vueuse/core'
import { CRUD, CustomModalProps, DataActions, DataType } from '@/interfaces'
import { VIDEO_ACTIONS_INJECTION_KEY } from '@/constants'
import { ModalType } from '@/components/modals/modals-util'
import { apiService } from '@/services'
import { useStore } from 'vuex'
import { EMPTY_FIELD } from '@/services/constants'
import generateIcon from '@/assets/popups/generate.svg'
import GeneratePlaylistModal from '@/components/modals/generateplaylist/GeneratePlaylistModal.vue'
import i18n from '@/i18n'
import { IModalProps } from '@/store/custom-modal.module'
import Label from '@/components/common/label/Label.vue'
import { LabelColors, LabelSizes } from '@/components/common/label/utils'

const props = defineProps(['item', 'columns'])
const { t } = useI18n()
const folderName = computed(() => props.item.folder?.name ?? EMPTY_FIELD)
const categoryName = computed(() => (props.item.category?.name ? t(`category_${props.item.category?.id}`) : EMPTY_FIELD))
const title = computed(() => props.item.title ?? EMPTY_FIELD)
const lastRun = computed(() => (props.item?.last_run ? getDateString(props.item.last_run) : EMPTY_FIELD))
const lastRunNumOfErrors = computed(() => (props.item?.last_run || props.item?.last_run_feed_status_valid ? props.item?.last_run_number_of_errors : EMPTY_FIELD))
const createdDate = computed(() => (props.item?.created_date ? getDateString(props.item.created_date) : EMPTY_FIELD))
const router = useRouter()
const dataViewRow = ref()
const isRowHovered = useElementHover(dataViewRow)
const store = useStore()

const getStatusColor = (status: string): LabelColors => {
  switch (status) {
    case 'active':
      return LabelColors.Green
    case 'rejected':
      return LabelColors.Red
    case 'inactive':
    case 'pending':
    default:
      return LabelColors.Grey
  }
}

const getPublicStatusColor = (isPublic: boolean): LabelColors => {
  return isPublic ? LabelColors.Green : LabelColors.Grey
}

const actions = [
  {
    label: 'playlist',
    handler: generatePlaylist,
    translationKey: 'generate-playlist'
  },
  {
    label: 'edit',
    handler: editStream,
    translationKey: 'edit'
  },
  {
    label: 'delete',
    handler: openDeleteStream,
    translationKey: 'stream-delete'
  }
]
const modalType = ModalType.STREAMS_DELETE_STREAM_MODAL
const dataActions = inject<DataActions>(VIDEO_ACTIONS_INJECTION_KEY)
const deleteStream = async (params) => {
  return apiService.videos.deleteStream(params.id).then(() => {
    if (dataActions?.refreshData) dataActions.refreshData()
  })
}

function editStream() {
  router.push({
    path: `/videos/content-source/edit/${props.item.id}`,
    name: 'cd-edit-stream',
    params: { id: props.item.id }
  })
}

async function generatePlaylist() {
  const modalProps: CustomModalProps = {
    headerProps: {
      text: i18n.global.t('generate-playlist-title'),
      textSecondary: i18n.global.t('generate-playlist-subtitle'),
      icon: generateIcon
    },
    footerProps: {
      buttonCancel: i18n.global.t('cancel'),
      buttonConfirm: i18n.global.t('generate')
    },
    showDiscardStep: false
  }
  const modal: IModalProps = {
    modalType: ModalType.GENERATE_PLAYLIST_MODAL,
    componentContent: markRaw(GeneratePlaylistModal),
    props: { ...modalProps },
    payload: {
      stream_id: props.item.id,
      name: props.item.title || '',
      description: ''
    }
  }
  store.dispatch('modal/openModal', modal)
}

function openDeleteStream() {
  openModalCustomConfirmation({ action: CRUD.Delete, type: DataType.ContentSources, id: props.item.id, onConfirm: deleteStream }, modalType)
}
</script>

<style lang="scss" scoped>
.flex {
  display: flex;
}

.approved {
  width: 100%;
}

.head {
  padding: 16px;

  :deep(.label-container) {
    width: 112px;
  }
}

.grid {
  display: grid;
}

.widget {
  .row-content {
    align-items: center;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0.1px;
    color: #4c4c66;
    box-sizing: border-box;
    text-overflow: ellipsis;
    height: 60px;
    cursor: pointer;

    .type {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      font-family: 'Nunito';
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 22px;
      /* identical to box height */
      letter-spacing: 0.1px;
      width: 130px;
      height: 28px;

      background: #f2f2f2;
      border-radius: 4px;

      color: #6f6e71;
      white-space: nowrap;
    }
  }

  @media (max-width: $xl) {
    height: auto;
    padding: 10px 20px;
  }

  @media (max-width: $lg) {
    height: auto;
    padding: 10px 20px;
  }

  @media (max-width: $md) {
    height: auto;
    padding: 10px 20px;
  }

  .device-type {
    border: 1px solid $inputBorder;
    border-radius: 4px;
    font-family: 'Nunito';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    /* identical to box height */
    text-align: center;
    text-transform: capitalize;
    /* body text */
    color: #4c4c66;
    opacity: 0.7;
    padding: 4px 12px;
    margin-right: 8px;
  }

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 10px;
    white-space: nowrap;
  }

  &:hover {
    background-color: #f3f4fa;
  }
}

.category-bg {
  display: flex;
  align-items: center;
  border-radius: 4px;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.1px;
  height: 28px;
  white-space: nowrap;
}

.title {
  display: grid;
  grid-template-columns: 100%;
  padding-inline: 1rem;
  overflow: hidden;
}
</style>
